import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'design-system/Modal';
import { useQuery } from 'react-query';
import {
  getContributorResume,
  retriggerResumeParse,
  removeUserResume,
} from 'react-scale/corp/lib/api/growth';
import { Spinner, TextArea, Tag, Button, Icon } from '@scale/scaleui';
import { IUserJson } from '@scale/shared/types/user';
import { ResumeParsingStatus } from '@scale/shared/types/demographics';

const blobToPDFObjectURL = (blob: Blob) =>
  URL.createObjectURL(blob.slice(0, blob.size, 'application/pdf'));

const fileSignatureCheck = async (blob: Blob): Promise<string> => {
  const maxBytes = 50;
  const firstChunk = blob.slice(0, maxBytes);
  const buffer = await firstChunk.arrayBuffer();
  const view = new Uint8Array(buffer);
  const text = await firstChunk.text();

  if (text.startsWith('%PDF')) return 'pdf';
  if (view[0] === 0x50 && view[1] === 0x4b) return 'docx';

  return 'unknown';
};

const TaskerResumeModal: React.FC<{
  isModalOpen: boolean;
  hideModal: () => void;
  resume?: string;
  setResumeResponse: ({
    resume,
    resumeNotFound,
  }: {
    resume: string;
    resumeNotFound: boolean;
  }) => void;
  user: IUserJson;
  // eslint-disable-next-line complexity
}> = ({ isModalOpen, hideModal, resume, setResumeResponse, user }) => {
  const [resumeLoading, setResumeLoading] = useState<boolean>(true); // Using a separate loading state because isLoading from useQuery becomes false before onSuccess finishes
  const [resumeUrl, setResumeUrl] = useState<string>('');
  const [resumeParsingStatus, setResumeParsingStatus] = useState<ResumeParsingStatus | null>(null);
  const [resumeMetadataGenerationStatus, setResumeMetadataGenerationStatus] =
    useState<ResumeParsingStatus | null>(null);
  const [fileType, setFileType] = useState<string>('');
  const [resumeText, setResumeText] = useState<string>('');
  const [translatedResumeText, setTranslatedResumeText] = useState<string | undefined>(undefined);
  const [currentTab, setCurrentTab] = useState<'resume' | 'resumeText' | 'translatedResumeText'>(
    'resume',
  );
  const [triggeredResumeParse, setTriggeredResumeParse] = useState<boolean>(false);
  const [requestedInfoText, setRequestedInfoText] = useState<boolean>(false);
  const [showConfirmRemoveResume, setShowConfirmRemoveResume] = useState<boolean>(false);

  const { refetch } = useQuery(
    ['getContributorResume', user?._id],
    async () => {
      setResumeLoading(true);
      setTriggeredResumeParse(false);
      setShowConfirmRemoveResume(false);
      return getContributorResume(user._id).catch(() => {
        setResumeResponse({ resume: '', resumeNotFound: true });
        setResumeLoading(false);
        return {
          resume: '',
          resumeNotFound: true,
        };
      });
    },
    {
      onSuccess: async (data: {
        resumeText?: string;
        translatedResumeText?: string;
        resumeUrl?: string;
        resumeParsingStatus?: ResumeParsingStatus;
        resumeMetadataGenerationStatus?: ResumeParsingStatus;
      }) => {
        const resumeUrl = data?.resumeUrl;
        setResumeText(data?.resumeText || '');
        setTranslatedResumeText(data?.translatedResumeText);
        setResumeUrl(resumeUrl || '');
        setResumeParsingStatus(data?.resumeParsingStatus || null);
        setResumeMetadataGenerationStatus(data?.resumeMetadataGenerationStatus || null);
        if (resumeUrl) {
          const resumeBlob = await fetch(resumeUrl).then(res => res.blob());
          const fileType = await fileSignatureCheck(resumeBlob);
          setFileType(fileType);
          setResumeResponse({ resume: blobToPDFObjectURL(resumeBlob), resumeNotFound: false });
        }
        setResumeLoading(false);
      },
      refetchOnWindowFocus: false,
      enabled: isModalOpen && !resume,
    },
  );

  const onClickTriggerResumeParse = () => {
    void retriggerResumeParse(user._id);
    setTriggeredResumeParse(true);
  };

  const onClickRemoveResume = async () => {
    void removeUserResume(user._id);
    setTimeout(() => {
      refetch();
    }, 2000);
  };

  const toggleTextOrFileShowing = () => {
    if (currentTab === 'resume') {
      setCurrentTab('resumeText');
    } else if (translatedResumeText && currentTab === 'resumeText') {
      setCurrentTab('translatedResumeText');
    } else {
      setCurrentTab('resume');
    }
  };

  const labelForTab = (tab: 'resume' | 'resumeText' | 'translatedResumeText') => {
    if (tab === 'resume') {
      return 'View Parsed Text';
    } else if (tab === 'resumeText' && translatedResumeText) {
      return 'View English Translation';
    } else {
      return 'View Resume File';
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      contentLabel="Contributor Resume"
      onRequestClose={hideModal}
      minWidth="50%"
      minHeight="75%"
    >
      <ModalHeader onRequestClose={hideModal}>
        Resume for {user.firstName || 'no_first_name'} {user.lastName || 'no_last_name'}
      </ModalHeader>
      <ModalBody padding="8px 4px 4px 4px">
        <div className="flex flex-col items-center w-full h-[75vh] scaleui scale-tailwind outlier-theme">
          {resumeLoading && <Spinner size="lg" />}
          {!resumeLoading && !resume && (
            <div> This contributor doesn't have a resume uploaded. </div>
          )}
          {!resumeLoading && (
            <div className="flex flex-col gap-y-1 mb-2 ml-4 self-start">
              <div className="flex items-center gap-x-4">
                <div>Status:</div>
                <div className="flex gap-x-1 ml-2">
                  <Tag className="w-max" variant="primary">
                    parsing-{resumeParsingStatus || 'n/a'}
                  </Tag>
                  <Tag className="w-max" variant="primary">
                    metadata-generation-{resumeMetadataGenerationStatus || 'n/a'}
                  </Tag>
                  <Icon
                    icon="info-circle"
                    className="cursor-pointer"
                    onClick={() => setRequestedInfoText(!requestedInfoText)}
                  />
                  <Icon icon="refresh" className="cursor-pointer" onClick={() => refetch()} />
                </div>
              </div>
              <div className="flex items-center gap-x-4">
                <div>Actions:</div>
                <div className="flex gap-x-2">
                  <Button variant="secondary" onClick={() => toggleTextOrFileShowing()}>
                    {labelForTab(currentTab)}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => onClickTriggerResumeParse()}
                    disabled={triggeredResumeParse || !resume}
                  >
                    {triggeredResumeParse ? 'Parse in progress' : 'Retrigger resume parse'}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setShowConfirmRemoveResume(true)}
                    disabled={triggeredResumeParse || showConfirmRemoveResume}
                  >
                    Remove resume
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!resumeLoading && (triggeredResumeParse || requestedInfoText) && (
            <div className="p-4">
              Users will be able to proceed through onboarding once both statuses show success.
              Resumes usually take ~2 minutes to go through the full flow. Refresh and check back
              here for parsing & metadata status to be updated after that time. Only use the
              retrigger option if you're sure the Contributor has been stuck for at least 2 minutes
              with no updates on the Resume Collection screen.
            </div>
          )}

          {!resumeLoading && showConfirmRemoveResume && (
            <div className="p-4 flex-col">
              <div className="mb-3">
                This action is <strong>not reversible</strong>, the contributor will need to upload
                a new resume. Are you sure you want to proceed?
              </div>
              <div className="flex gap-x-2">
                <Button
                  variant="danger"
                  onClick={() => onClickRemoveResume()}
                  disabled={triggeredResumeParse}
                >
                  Remove
                </Button>
                <Button
                  variant="neutral"
                  onClick={() => setShowConfirmRemoveResume(false)}
                  disabled={triggeredResumeParse}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {!resumeLoading && currentTab === 'resume' && (
            <div className="w-full h-full">
              {resume && fileType === 'pdf' ? (
                <object
                  data={resume}
                  type="application/pdf"
                  style={{ width: '100%', height: '100%' }}
                >
                  This file could not be displayed: download{' '}
                  <a className="ml-1 underline" href={resumeUrl}>
                    here
                  </a>
                  .
                </object>
              ) : (
                <div>
                  This file could not be displayed: download{' '}
                  <a className="ml-1 underline" href={resumeUrl}>
                    here
                  </a>
                  .
                </div>
              )}
            </div>
          )}
          {resume && currentTab === 'resumeText' && (
            <>
              {resumeParsingStatus !== ResumeParsingStatus.Successful && resumeText?.length && (
                <div>Showing naive pdf-parse results:</div>
              )}
              <TextArea
                value={resumeText || 'has no resume text'}
                readOnly
                resizable={false}
                initialResize
                className="w-full h-full"
                textAreaClassName="focus:ring-0 focus:border-neutral-300 h-full"
                textAreaWrapperClassName="h-full"
              />
            </>
          )}
          {resume && translatedResumeText && currentTab === 'translatedResumeText' && (
            <>
              <TextArea
                value={translatedResumeText}
                readOnly
                resizable={false}
                initialResize
                className="w-full h-full"
                textAreaClassName="focus:ring-0 focus:border-neutral-300 h-full"
                textAreaWrapperClassName="h-full"
              />
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default TaskerResumeModal;
